import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Spinner, Alert, Table } from 'react-bootstrap';
import { Download } from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import {
  ASSET_STATUS_META,
  csvNumber,
  downloadCsv,
  formatDate,
  formatNumber,
  getAssetAccountingUnavailable,
  getCategoryMeta,
  round2,
  toCsvValue,
  unwrapData,
} from '../../Accounting/Assets/assetUtils';

// Column model: the BFNAR 2013:2 punkt 4.5 register fields (backend supplies
// the data; this page only renders/export them) plus the forecast column.
const num = (value) => parseFloat(value) || 0;

const COLUMNS = [
  { key: 'asset_number', label: 'Nr', render: (row) => row.asset_number ?? '—' },
  { key: 'name', label: 'Benämning', render: (row) => row.name },
  {
    key: 'category',
    label: 'Kategori',
    render: (row) => {
      const meta = getCategoryMeta(row.category_code || row.category);
      return row.category_name || meta.label;
    },
    csv: (row) => row.category_name || getCategoryMeta(row.category_code || row.category).label,
  },
  { key: 'acquisition_value', label: 'Anskaffningsvärde', numeric: true, value: (row) => num(row.acquisition_value) },
  { key: 'acquisition_date', label: 'Anskaffningsdatum', render: (row) => formatDate(row.acquisition_date), csv: (row) => row.acquisition_date || '' },
  { key: 'residual_value', label: 'Restvärde', numeric: true, value: (row) => num(row.residual_value) },
  {
    key: 'useful_months',
    label: 'Nyttjandeperiod',
    render: (row) => (row.useful_months ? `${Math.round(row.useful_months / 12)} år` : '—'),
    csv: (row) => (row.useful_months ? `${row.useful_months} mån` : ''),
  },
  { key: 'accumulated_depreciation', label: 'Ack. avskrivningar', numeric: true, value: (row) => num(row.accumulated_depreciation ?? row.acc_depreciation) },
  { key: 'book_value', label: 'Bokfört värde', numeric: true, value: (row) => num(row.book_value ?? row.remaining_value) },
  {
    key: 'monthly_depreciation',
    label: 'Prognos per månad',
    numeric: true,
    nullable: true,
    value: (row) => (row.monthly_depreciation == null ? null : num(row.monthly_depreciation)),
  },
  {
    key: 'status',
    label: 'Status',
    render: (row) => (ASSET_STATUS_META[row.status]?.label || row.status || '—'),
    csv: (row) => ASSET_STATUS_META[row.status]?.label || row.status || '',
  },
];

/**
 * AssetRegisterReport — anläggningsregistret som rapport: en rad per
 * tillgång med BFNAR-fälten, summeringsrad och klient-genererad CSV-export.
 */
const AssetRegisterReport = () => {
  const { companyId } = useParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accountingUnavailable, setAccountingUnavailable] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAccountingUnavailable(null);
    try {
      const response = await axios.get(getApiUrl(`${companyId}/assets/reports/register`), { withCredentials: true });
      const data = unwrapData(response);
      setRows(Array.isArray(data) ? data : data?.assets || data?.rows || []);
      setAccountingUnavailable(getAssetAccountingUnavailable(data?.accountingCapability));
    } catch (err) {
      const unavailable = getAssetAccountingUnavailable(err);
      setError(unavailable?.message || 'Det gick inte att hämta anläggningsregistret. Försök igen senare.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const totals = useMemo(() => {
    const sums = {};
    COLUMNS.filter((c) => c.numeric).forEach((column) => {
      sums[column.key] = round2(rows.reduce((acc, row) => acc + (column.value(row) ?? 0), 0));
    });
    return sums;
  }, [rows]);

  const handleExportCsv = () => {
    const header = COLUMNS.map((c) => toCsvValue(c.label)).join(';');
    const lines = rows.map((row) =>
      COLUMNS.map((column) => {
        if (column.numeric) {
          const value = column.value(row);
          return value == null ? '' : csvNumber(value);
        }
        if (column.csv) return toCsvValue(column.csv(row));
        return toCsvValue(column.render(row));
      }).join(';')
    );
    const totalsLine = COLUMNS.map((column, index) => {
      if (index === 0) return 'Summa';
      if (column.numeric) return csvNumber(totals[column.key]);
      return '';
    }).join(';');

    downloadCsv(
      [header, ...lines, totalsLine],
      `anlaggningsregister-${new Date().toISOString().split('T')[0]}.csv`
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: 400 }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Laddar anläggningsregistret…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-3">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h3 style={{ fontWeight: 700, color: 'var(--title)', marginBottom: 2 }}>Anläggningsregister</h3>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
            Företagets tillgångar med anskaffningsvärden, avskrivningar och bokförda värden.
          </p>
        </div>
        <Button
          variant="outline-primary"
          className="d-flex align-items-center gap-1"
          onClick={handleExportCsv}
          disabled={rows.length === 0}
        >
          <Download size={16} /> Exportera CSV
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {accountingUnavailable && (
        <Alert variant="warning">
          <Alert.Heading>{accountingUnavailable.heading}</Alert.Heading>
          <p className="mb-0">
            Registerfält och bokförda värden visas och kan exporteras. Prognoskolumnen
            lämnas tom eftersom den kräver en redovisningsmodell som inte stöds.
          </p>
        </Alert>
      )}

      <div className="modern-card">
        <div style={{ padding: '0.5rem 0.5rem', overflowX: 'auto' }}>
          {rows.length === 0 && !error ? (
            <p className="text-muted text-center py-5 mb-0">
              Registret är tomt — lägg till tillgångar under Tillgångar så byggs rapporten upp automatiskt.
            </p>
          ) : (
            <Table hover responsive size="sm" className="align-middle mb-0">
              <thead>
                <tr>
                  {COLUMNS.map((column) => (
                    <th key={column.key} className={column.numeric ? 'text-end' : ''} style={{ whiteSpace: 'nowrap' }}>
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.asset_id || index}>
                    {COLUMNS.map((column) => (
                      <td key={column.key} className={column.numeric ? 'text-end' : ''} style={{ whiteSpace: 'nowrap' }}>
                        {column.numeric
                          ? column.value(row) == null ? '—' : formatNumber(column.value(row))
                          : column.render(row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700, borderTop: '2px solid #e2e8f0' }}>
                  {COLUMNS.map((column, index) => (
                    <td key={column.key} className={column.numeric ? 'text-end' : ''}>
                      {index === 0
                        ? 'Summa'
                        : column.numeric && !column.nullable ? formatNumber(totals[column.key]) : ''}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetRegisterReport;
